/**
 * FeedbackOverlay — Commander Control Room aesthetic
 *
 * Full-screen overlay for showing success / error / warning feedback after
 * a kiosk action. Auto-dismisses after a timeout.
 *
 * Visual language: the panel matches the modal family — 2px coloured top
 * rail, sharp corners, hairline borders. Icon is a single large glyph
 * (not a chip), title in Geist semibold, message in Geist secondary,
 * dismiss hint in JetBrains Mono uppercase.
 */

import { useEffect } from 'react';
import { Box, Center, Overlay, Stack } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconX } from '@tabler/icons-react';

import { eyebrowStyle, titleStyle } from './modalStyles';

export type FeedbackType = 'success' | 'error' | 'warning';

const FEEDBACK_LABELS: Record<FeedbackType, string> = {
  success: 'Complete',
  error: 'Failed',
  warning: 'Attention',
};

export interface FeedbackOverlayProps {
  visible: boolean;
  type: FeedbackType;
  title: string;
  message?: string;
  /** Auto-dismiss timeout in milliseconds (0 to disable). */
  autoHideMs?: number;
  onHide: () => void;
}

const feedbackConfig: Record<
  FeedbackType,
  { rail: string; Icon: typeof IconCheck }
> = {
  success: { rail: 'var(--status-online-accent)', Icon: IconCheck },
  error:   { rail: 'var(--status-error-accent)',  Icon: IconX },
  warning: { rail: 'var(--status-warning-accent)', Icon: IconAlertTriangle },
};

export function FeedbackOverlay({
  visible,
  type,
  title,
  message,
  autoHideMs = 3000,
  onHide,
}: FeedbackOverlayProps) {
  useEffect(() => {
    if (visible && autoHideMs > 0) {
      const timer = setTimeout(onHide, autoHideMs);
      return () => clearTimeout(timer);
    }
  }, [visible, autoHideMs, onHide]);

  if (!visible) return null;

  const { rail, Icon } = feedbackConfig[type];

  return (
    <Overlay
      fixed
      backgroundOpacity={0.85}
      zIndex={1000}
      onClick={onHide}
      data-testid="feedback-overlay"
      data-type={type}
    >
      <Center h="100vh">
        <Box
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-colour)',
            borderTop: `2px solid ${rail}`,
            borderRadius: 'var(--radius-sm, 4px)',
            maxWidth: 460,
            width: '100%',
            padding: '28px 32px 24px',
            textAlign: 'left',
          }}
        >
          <Stack gap={18}>
            <Box
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 44,
                height: 44,
                borderRadius: 'var(--radius-sm, 4px)',
                background: 'var(--bg-base)',
                color: rail,
              }}
            >
              <Icon size={28} stroke={2} aria-hidden />
            </Box>

            <Stack gap={6}>
              <div style={{ ...eyebrowStyle(rail, 0) }}>{FEEDBACK_LABELS[type]}</div>
              <div style={{ ...titleStyle(22), lineHeight: 1.2 }}>{title}</div>
              {message ? (
                <div
                  style={{
                    fontSize: 14,
                    lineHeight: 1.5,
                    color: 'var(--text-secondary)',
                    marginTop: 4,
                  }}
                >
                  {message}
                </div>
              ) : null}
            </Stack>

            <Box
              style={{
                paddingTop: 12,
                borderTop: '1px solid var(--border-colour)',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
              }}
            >
              Tap anywhere to dismiss
            </Box>
          </Stack>
        </Box>
      </Center>
    </Overlay>
  );
}
